/**
 * useVisitActions - Hook providing all visit action mutations
 *
 * Matches v2 modal-new-appointment.js actions:
 * - updateVisit (lines 1307-1395)
 * - updateVisitsWithConfirmation (lines 1291-1305)
 * - updateVisitPaid (lines 1427-1445)
 * - bulkChargeVisits (lines 277-340)
 * - deleteVisit (lines 503-549)
 */
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useSession } from 'src/contexts/SessionContext';
import { isTrainer } from 'src/models/user/utils';
import { VisitStatus } from 'src/models/visit/_types';
import {
  bulkChargeDefaultPackage,
  bulkUpdateVisits,
  deleteVisit,
  resetVisit,
  updateVisit,
} from 'src/models/visit/visit';
import { CheckinBlockerOverrideContext } from 'src/shared/CheckinBlocker/_types';
import { useCheckInBlocker } from 'src/shared/CheckinBlocker/useCheckInBlocker';
import api from 'src/utils/ajax';
import toast from 'src/utils/toast';

import type { Appointment } from 'src/models/appointment/_types';
import type { Visit } from 'src/models/visit/_types';
import type { Identifier } from 'src/types/utilityTypes';

interface UseVisitActionsOptions {
  appointment: Appointment;
  onVisitUpdate?: (visit: Visit) => void;
  /**
   * Callback to refresh the appointment data after mutations.
   * Can be async - will be awaited by bulk operations before showing success toast.
   */
  onAppointmentRefresh?: () => void | Promise<void>;
  /**
   * Confirmation function for fee-charging actions.
   * Should return true if user confirms, false to cancel.
   * Matches v2's updateVisitsWithConfirmation pattern.
   */
  confirmFeeCharge?: (message: string) => Promise<boolean>;
  /**
   * Confirmation function for delete/remove actions.
   * Should return true if user confirms, false to cancel.
   */
  confirmDelete?: (message: string) => Promise<boolean>;
}

export const useVisitActions = ({
  appointment,
  onVisitUpdate,
  onAppointmentRefresh,
  confirmFeeCharge,
  confirmDelete,
}: UseVisitActionsOptions) => {
  const { t } = useTranslation('translation', {
    keyPrefix: 'calendar.viewEventDialog.visitActions',
  });
  const { t: tCheckinBlocker } = useTranslation('translation', {
    keyPrefix: 'checkinBlocker',
  });
  const { user } = useSession();
  const queryClient = useQueryClient();

  const pendingVisitsRef = useRef<Visit[]>([]);
  const onCloseRef = useRef<() => void>(() => {});

  // Invalidate relevant queries after mutations
  const invalidateQueries = async (visitId?: Identifier, skipAppointmentRefresh = false) => {
    queryClient.invalidateQueries({ queryKey: ['appointments'] });
    queryClient.invalidateQueries({ queryKey: ['calendar'] });
    queryClient.invalidateQueries({ queryKey: ['visits'] });
    // Invalidate package queries for the specific visit, or all if no visitId
    if (visitId) {
      queryClient.invalidateQueries({ queryKey: ['visitPackage', visitId] });
      queryClient.invalidateQueries({ queryKey: ['visitAvailablePackages', visitId] });
    } else {
      queryClient.invalidateQueries({ queryKey: ['visitPackage'] });
      queryClient.invalidateQueries({ queryKey: ['visitAvailablePackages'] });
    }
    // Await appointment refresh if it returns a Promise to ensure UI updates before success feedback
    if (!skipAppointmentRefresh) await onAppointmentRefresh?.();
  };

  // Single visit status update mutation
  const updateVisitMutation = useMutation({
    mutationFn: async ({
      visitId,
      status,
      chargeCancellation,
      chargeNoshow,
    }: {
      visitId: Identifier;
      status: VisitStatus | null; // null means reset
      chargeCancellation?: boolean;
      chargeNoshow?: boolean;
      skipAppointmentRefresh?: boolean;
    }) => {
      // Reset visit uses different endpoint
      if (status === null) {
        await resetVisit(visitId);
        return { visitId };
      }

      const visitData: Partial<Visit> = {
        id: visitId,
        status,
      };

      // Add fee charging flags if specified
      if (chargeCancellation) visitData.chargeCancellation = true;
      if (chargeNoshow) visitData.chargeNoshow = true;

      const result = await updateVisit(visitData);
      return { ...result, visitId };
    },
    onMutate: ({ visitId, status, skipAppointmentRefresh }) => {
      if (!skipAppointmentRefresh || status === null) return undefined;
      const originalVisit = (appointment.visits || []).find((v) => v.id === visitId);
      if (originalVisit) {
        onVisitUpdate?.({ ...originalVisit, status });
      }
      return { originalVisit };
    },
    onSuccess: (result, variables) => {
      // If optimistic update was applied, replace with real server data
      if (result && 'id' in result) onVisitUpdate?.(result as Visit);
      invalidateQueries(result?.visitId, variables.skipAppointmentRefresh);
      toast.success(t('visitUpdatedSuccessfully'));
    },
    onError: (error, variables, context) => {
      // Roll back optimistic update if present
      if (context?.originalVisit) {
        onVisitUpdate?.(context.originalVisit);
      }
      toast.error(api.formatError(error, t('failedToUpdateVisit')));
    },
  });

  // Bulk visit status update mutation
  const bulkUpdateMutation = useMutation({
    mutationFn: async ({
      visitIds,
      status,
      cancelRecurring = false,
    }: {
      visitIds: Identifier[];
      status: VisitStatus | null; // null means reset
      cancelRecurring?: boolean;
    }) => {
      if (status === null) {
        // Reset all visits
        await Promise.all(visitIds.map((id) => resetVisit(id)));
        return null;
      }
      return bulkUpdateVisits({ visitIds, status, cancelRecurring });
    },
    onSuccess: async () => {
      // Await the refresh to ensure UI updates before showing toast
      await invalidateQueries();
      toast.success(t('allVisitsUpdatedSuccessfully'));
    },
    onError: (error) => {
      toast.error(api.formatError(error, t('failedToUpdateVisits')));
    },
  });

  // Resolve which pending visit(s) a given override action applies to.
  const getOverrideVisits = (context: CheckinBlockerOverrideContext) => {
    const visits = pendingVisitsRef.current;
    if (context.scope === 'all' || !context.user) return visits;

    return visits.filter(
      (visit) => String(visit.userId) === String(context.user?.id)
    );
  };

  const [checkInBlockerDialogProps, checkBlockers] = useCheckInBlocker({
    title: tCheckinBlocker('actionRequired'),
    onOverride: isTrainer(user)
      ? async (context) => {
          const visits = getOverrideVisits(context);
          if (visits.length === 0) return;

          if (visits.length > 1) {
            await bulkUpdateMutation.mutateAsync({
              visitIds: visits.map((v) => v.id),
              status: VisitStatus.Completed,
            });
          } else {
            await updateVisitMutation.mutateAsync({
              visitId: visits[0].id,
              status: VisitStatus.Completed,
            });
          }

          if (context.scope === 'all' || !context.user) {
            onCloseRef.current();
          }
        }
      : undefined,
  });

  onCloseRef.current = checkInBlockerDialogProps.onClose;

  // Bulk charge default package mutation
  const bulkChargeMutation = useMutation({
    mutationFn: async ({
      visitIds,
      completeVisit = true,
    }: {
      visitIds: Identifier[];
      completeVisit?: boolean;
    }) => {
      const result = await bulkChargeDefaultPackage({ visitIds, completeVisit });
      return { ...result, isSingleVisit: visitIds.length === 1 };
    },
    onSuccess: async (result) => {
      // Await the refresh to ensure UI updates with new package data before showing toast
      await invalidateQueries();

      if (result.isSingleVisit) {
        // Single visit charge
        if (result.status === 'pending') {
          toast.success(t('subscriptionPending'));
        } else {
          toast.success(t('visitChargedAndCompleted'));
        }
      } else {
        // Bulk charge
        toast.success(t('bulkChargeInProgress'));
      }
    },
    onError: (error) => {
      toast.error(api.formatError(error, t('failedToChargeVisits')));
    },
  });

  // Package deduction toggle mutation (v2 updateVisitPaid)
  const updatePackageDeductionMutation = useMutation({
    mutationFn: async ({
      visitId,
      paidByPackageId,
    }: {
      visitId: Identifier;
      paidByPackageId: Identifier | null;
    }) => {
      const result = await updateVisit({
        id: visitId,
        paidByPackageId: paidByPackageId,
        paid: !paidByPackageId, // If removing package, mark as paid anyway
      });
      return { ...result, visitId };
    },
    onSuccess: (result) => {
      if (result && 'id' in result) onVisitUpdate?.(result as Visit);
      invalidateQueries(result?.visitId);
      if (result?.paidByPackageId) {
        toast.success(t('packageDeductionApplied'));
      } else {
        toast.success(t('packageDeductionRemoved'));
      }
    },
    onError: (error) => {
      toast.error(api.formatError(error, t('failedToUpdatePackageDeduction')));
    },
  });

  // Delete/remove visit mutation
  const deleteVisitMutation = useMutation({
    mutationFn: async (visitId: Identifier) => {
      await deleteVisit(visitId);
      return { visitId };
    },
    onSuccess: async (result) => {
      // Await the refresh to ensure UI updates before showing toast
      await invalidateQueries(result?.visitId);
      toast.success(t('visitRemovedFromSession'));
    },
    onError: (error) => {
      toast.error(api.formatError(error, t('failedToRemoveVisit')));
    },
  });

  // === ACTION HANDLERS ===

  // Single visit status actions
  const handleMarkCompleted = async (visit: Visit) => {
    pendingVisitsRef.current = [visit];
    const userIds = [visit.userId].filter(Boolean) as Identifier[];
    if (userIds.length > 0) {
      const blockers = await checkBlockers({ userIds });
      if (blockers.length > 0) return;
    }

    updateVisitMutation.mutate({
      visitId: visit.id,
      status: VisitStatus.Completed,
    });
  };

  const handleMarkNoShow = async (visit: Visit, withFee = false) => {
    // If charging a fee, confirm first (matches v2 updateVisitsWithConfirmation)
    if (withFee && confirmFeeCharge) {
      const confirmed = await confirmFeeCharge(
        'Are you sure you want to mark this session as no show AND charge a fee?'
      );
      if (!confirmed) return;
    }
    updateVisitMutation.mutate({
      visitId: visit.id,
      status: VisitStatus.NoShowed,
      chargeNoshow: withFee,
    });
  };

  const handleMarkCancelled = (visit: Visit) => {
    updateVisitMutation.mutate({
      visitId: visit.id,
      status: VisitStatus.Cancelled,
      skipAppointmentRefresh: true,
    });
  };

  const handleMarkLateCancelled = async (visit: Visit, withFee = false) => {
    // If charging a fee, confirm first (matches v2 updateVisitsWithConfirmation)
    if (withFee && confirmFeeCharge) {
      const confirmed = await confirmFeeCharge(
        'Are you sure you want to mark this session as late cancelled AND charge a fee?'
      );
      if (!confirmed) return;
    }
    updateVisitMutation.mutate({
      visitId: visit.id,
      status: VisitStatus.LateCancelled,
      chargeCancellation: withFee,
      skipAppointmentRefresh: true,
    });
  };

  const handleResetStatus = (visit: Visit) => {
    updateVisitMutation.mutate({ visitId: visit.id, status: null });
  };

  const handleConfirmGoing = (visit: Visit) => {
    updateVisitMutation.mutate({
      visitId: visit.id,
      status: VisitStatus.Registered,
    });
  };

  // Package actions
  const handleDontUsePackage = (visit: Visit) => {
    updatePackageDeductionMutation.mutate({
      visitId: visit.id,
      paidByPackageId: null,
    });
  };

  const handleAttemptPackageDeduction = (visit: Visit, packageId?: Identifier) => {
    // If no packageId provided, the backend will try to find one
    updatePackageDeductionMutation.mutate({
      visitId: visit.id,
      paidByPackageId: packageId || ('auto' as unknown as Identifier), // Signal to backend to auto-find
    });
  };

  // Charge default package and complete
  const handleChargeDefaultPackageAndComplete = (visit: Visit) => {
    bulkChargeMutation.mutate({
      visitIds: [visit.id],
      completeVisit: true,
    });
  };

  // Delete/remove visit
  const handleDeleteVisit = async (visit: Visit) => {
    if (confirmDelete) {
      const confirmed = await confirmDelete(
        `Are you sure you want to cancel and remove ${visit.userName || 'this visit'} from the session? This action cannot be undone.`
      );
      if (!confirmed) return;
    }
    deleteVisitMutation.mutate(visit.id);
  };

  // === BULK ACTION HANDLERS ===

  const getActiveVisits = () => {
    return (appointment.visits || []).filter(
      (v) =>
        v.id &&
        v.status !== VisitStatus.Cancelled &&
        v.status !== VisitStatus.LateCancelled
    );
  };

  const getActiveVisitIds = () => {
    return getActiveVisits().map((v) => v.id);
  };

  const getAllVisitIds = () => {
    return (appointment.visits || [])
      .map((v) => v.id);
  };

  const handleBulkConfirmGoing = () => {
    const visitIds = getActiveVisitIds();
    if (visitIds.length > 0) {
      bulkUpdateMutation.mutate({ visitIds, status: VisitStatus.Registered });
    }
  };

  const handleBulkComplete = async () => {
    const visits = getActiveVisits();
    if (visits.length === 0) return;

    pendingVisitsRef.current = visits;
    const userIds = visits.map((v) => v.userId).filter(Boolean) as Identifier[];

    let visitsToComplete = visits;
    if (userIds.length > 0) {
      const remainingBlockedUsers = await checkBlockers({ userIds });
      if (remainingBlockedUsers.length > 0) {
        // onOverride already checked in any overridden users. Complete only
        // the visits whose users were not in the blocker result.
        const blockedOrOverriddenIds = new Set(remainingBlockedUsers.map((u) => String(u.id)));
        visitsToComplete = visits.filter(
          (v) => v.userId == null || !blockedOrOverriddenIds.has(String(v.userId)),
        );
        if (visitsToComplete.length === 0) return;
      }
    }

    bulkUpdateMutation.mutate({
      visitIds: visitsToComplete.map((v) => v.id),
      status: VisitStatus.Completed,
    });
  };

  const handleBulkCancel = () => {
    const visitIds = getActiveVisitIds();
    if (visitIds.length > 0) {
      bulkUpdateMutation.mutate({ visitIds, status: VisitStatus.Cancelled });
    }
  };

  const handleBulkLateCancelNoFee = () => {
    const visitIds = getActiveVisitIds();
    if (visitIds.length > 0) {
      bulkUpdateMutation.mutate({
        visitIds,
        status: VisitStatus.LateCancelled,
      });
    }
  };

  const handleBulkNoShowNoFee = () => {
    const visitIds = getActiveVisitIds();
    if (visitIds.length > 0) {
      bulkUpdateMutation.mutate({ visitIds, status: VisitStatus.NoShowed });
    }
  };

  const handleBulkResetStatus = () => {
    const visitIds = getAllVisitIds();
    if (visitIds.length > 0) {
      bulkUpdateMutation.mutate({ visitIds, status: null });
    }
  };

  const handleBulkChargeDefaultPackage = () => {
    const visitIds = getActiveVisitIds();
    if (visitIds.length > 0) {
      bulkChargeMutation.mutate({ visitIds, completeVisit: true });
    }
  };

  return {
    checkInBlockerDialogProps,

    // Loading states
    isUpdating:
      updateVisitMutation.isPending ||
      bulkUpdateMutation.isPending ||
      bulkChargeMutation.isPending ||
      updatePackageDeductionMutation.isPending ||
      deleteVisitMutation.isPending,

    // Single visit actions
    handleMarkCompleted,
    handleMarkNoShow,
    handleMarkCancelled,
    handleMarkLateCancelled,
    handleResetStatus,
    handleConfirmGoing,
    handleDontUsePackage,
    handleAttemptPackageDeduction,
    handleChargeDefaultPackageAndComplete,
    handleDeleteVisit,

    // Bulk actions
    handleBulkConfirmGoing,
    handleBulkComplete,
    handleBulkCancel,
    handleBulkLateCancelNoFee,
    handleBulkNoShowNoFee,
    handleBulkResetStatus,
    handleBulkChargeDefaultPackage,
  };
};

export type VisitActionHandlers = ReturnType<typeof useVisitActions>;
